import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface ShopPayment {
  id: number;
  amount: number | string | null;
  currency: string | null;
  method: string | null;
  status: string | null;
  reference: string | null;
  payload: string | null;
  created_at: string | null;
}

interface ParsedPayment extends ShopPayment {
  orderId: number | null;
  commissionPercent: number;
  commissionAmount: number;
  gatewayAmount: number | null;
  gatewayCurrency: string;
  paymentMethod: string;
  network: string;
  isPaid: boolean;
}

const PAID_RE = /paid|success|succes|succès|completed|confirm/i;

function parsePayment(row: ShopPayment): ParsedPayment {
  let payload: Record<string, unknown> = {};
  try {
    payload = row.payload ? (JSON.parse(row.payload) as Record<string, unknown>) : {};
  } catch {
    payload = {};
  }
  const amount = Number(row.amount) || 0;
  const percent = Number(payload.commission_percent) || 0;
  const commission = Number(payload.commission_amount) || Math.round(amount * percent) / 100;
  return {
    ...row,
    orderId: Number(payload.order_id) || null,
    commissionPercent: percent,
    commissionAmount: commission,
    gatewayAmount: Number(payload.gateway_amount) || null,
    gatewayCurrency: String(payload.gateway_currency || 'XOF'),
    paymentMethod: String(payload.payment_method || 'mobile_money'),
    network: String(payload.network || ''),
    isPaid: PAID_RE.test(String(row.status || '')),
  };
}

interface ReceivedPaymentsTabProps {
  merchantId: number | null;
  currency?: string;
}

export default function ReceivedPaymentsTab({ merchantId, currency }: ReceivedPaymentsTabProps) {
  const [rows, setRows] = useState<ParsedPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'pending'>('all');

  const load = useCallback(async () => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('zifek_payments')
        .select('id, amount, currency, method, status, reference, payload, created_at')
        .eq('user_id', merchantId)
        .eq('method', 'shop_feexpay')
        .order('created_at', { ascending: false })
        .limit(300);
      if (fetchError) throw fetchError;
      setRows(((data as ShopPayment[]) || []).map(parsePayment));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les paiements reçus');
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (statusFilter === 'paid') return rows.filter((r) => r.isPaid);
    if (statusFilter === 'pending') return rows.filter((r) => !r.isPaid);
    return rows;
  }, [rows, statusFilter]);

  const totals = useMemo(() => {
    const paid = rows.filter((r) => r.isPaid);
    const gross = paid.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const fees = paid.reduce((s, r) => s + r.commissionAmount, 0);
    return { gross, fees, net: gross - fees, count: paid.length };
  }, [rows]);

  const fmt = (n: number, code?: string | null) =>
    `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${code || currency || ''}`.trim();

  const statusBadge = (isPaid: boolean) =>
    isPaid
      ? 'bg-accent-100 text-accent-700'
      : 'bg-amber-50 text-amber-700';

  return (
    <div>
      {/* Synthèse encaissements */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { label: 'Encaissé brut', value: fmt(totals.gross), icon: 'ri-money-dollar-circle-line', color: 'bg-primary-100 text-primary-700' },
          { label: 'Frais plateforme', value: fmt(totals.fees), icon: 'ri-percent-line', color: 'bg-amber-50 text-amber-700' },
          { label: 'Net reçu', value: fmt(totals.net), icon: 'ri-wallet-3-line', color: 'bg-accent-100 text-accent-700' },
          { label: 'Paiements confirmés', value: String(totals.count), icon: 'ri-check-double-line', color: 'bg-secondary-100 text-secondary-800' },
        ].map((card) => (
          <div key={card.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-8 h-8 rounded-lg ${card.color} flex items-center justify-center mb-2`}>
              <i className={`${card.icon} text-sm`}></i>
            </div>
            <div className="text-base md:text-lg font-bold font-heading text-foreground-950 break-words">{card.value}</div>
            <div className="text-xs text-foreground-500 mt-0.5">{card.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { key: 'all', label: 'Tous' },
          { key: 'paid', label: 'Payés' },
          { key: 'pending', label: 'En attente' },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key as 'all' | 'paid' | 'pending')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
              statusFilter === f.key
                ? 'bg-primary-50 text-primary-700 border border-primary-200'
                : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto w-8 h-8 flex items-center justify-center rounded-full border border-background-200/70 bg-background-50 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          title="Rafraîchir"
        >
          <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 border border-background-200/70 rounded-lg bg-background-50">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
          <p className="text-sm text-foreground-600 mb-3">{error}</p>
          <button onClick={load} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer">
            Réessayer
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 border border-background-200/70 rounded-lg bg-background-50">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-secure-payment-line text-2xl text-foreground-400"></i>
          </div>
          <h3 className="text-base font-semibold text-foreground-800 mb-1">Aucun paiement en ligne reçu</h3>
          <p className="text-sm text-foreground-500 text-center max-w-sm">
            Dès qu’un client paie une commande en ligne (Mobile Money ou carte), le détail des frais apparaît ici.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => {
            const amount = Number(p.amount) || 0;
            const net = amount - p.commissionAmount;
            return (
              <div key={p.id} className="rounded-lg border border-background-200/70 bg-background-50 p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex items-center gap-3 min-w-0 md:w-56">
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${p.isPaid ? 'bg-accent-100 text-accent-700' : 'bg-amber-50 text-amber-700'}`}>
                      <i className={p.paymentMethod === 'card' ? 'ri-bank-card-line' : 'ri-smartphone-line'}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">
                        {p.paymentMethod === 'card' ? 'Carte bancaire' : `Mobile Money${p.network ? ` · ${p.network}` : ''}`}
                      </p>
                      <p className="text-[11px] text-foreground-400 truncate">
                        {p.created_at ? new Date(p.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 flex-1 min-w-0 text-xs">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-foreground-400">Montant</p>
                      <p className="font-semibold text-foreground-900">{fmt(amount, p.currency)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-foreground-400">Frais {p.commissionPercent > 0 ? `(${p.commissionPercent} %)` : ''}</p>
                      <p className="font-semibold text-amber-700">− {fmt(p.commissionAmount, p.currency)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-foreground-400">Net reçu</p>
                      <p className="font-semibold text-accent-700">{fmt(net, p.currency)}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 md:w-56 md:justify-end">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${statusBadge(p.isPaid)}`}>
                      {p.isPaid ? 'Payé' : 'En attente'}
                    </span>
                    {p.orderId && (
                      <Link
                        to="/dashboard/orders"
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-100 border border-background-200/70 text-[10px] font-medium text-foreground-700 hover:bg-background-200/60 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-file-list-3-line"></i>Commande #{p.orderId}
                      </Link>
                    )}
                  </div>
                </div>

                <div className="mt-3 pt-2.5 border-t border-background-200/60 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-foreground-400">
                  <span className="font-mono break-all">Réf. {p.reference || '—'}</span>
                  {p.gatewayAmount && (
                    <span>Débité : {Number(p.gatewayAmount).toLocaleString('fr-FR')} {p.gatewayCurrency}</span>
                  )}
                  <span>Taux appliqué : {p.commissionPercent || 0} %</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-4 text-[11px] text-foreground-400 flex items-start gap-1.5">
        <i className="ri-information-line mt-0.5"></i>
        Les frais Zifek et les frais FeexPay / Mobile Money sont inclus dans le pourcentage prélevé sur chaque transaction.
      </p>
    </div>
  );
}